import { ImageIcon } from "lucide-react-native";
import { cssInterop } from "nativewind";
import Svg, { Path, SvgProps } from "react-native-svg";
import { Box } from "../box";
import { Icon } from "../icon";
import {
  HORIZON_HILLS_CLASS,
  HORIZON_SUN_CLASS,
  placeholderStyle,
} from "./styles";
import type { BoxSize, LoadStatus } from "./utils";

// Below this side the hills turn into noise, so the placeholder shows an icon.
const ICON_ONLY_MAX_SIZE = 48;
const SUN_MAX_SIZE = 22;

// The hills are drawn with `currentColor`, so a `text-*` class tints them
// through the theme (dark mode and tenant configs included).
const TintedSvg = (props: SvgProps) => <Svg {...props} />;
cssInterop(TintedSvg, {
  className: { target: "style", nativeStyleToProp: { color: true } },
});

const IconArt = () => (
  <Icon as={ImageIcon} size={18} className="text-typography-400" />
);

const HorizonArt = ({ size }: { size: BoxSize }) => {
  const sunSize = Math.min(SUN_MAX_SIZE, size.width * 0.14, size.height * 0.2);
  return (
    <>
      <Box
        className={HORIZON_SUN_CLASS}
        style={{ width: sunSize, height: sunSize }}
      />
      <TintedSvg
        className={HORIZON_HILLS_CLASS}
        viewBox="0 0 100 40"
        preserveAspectRatio="none"
      >
        <Path
          d="M0 22 C 18 8, 34 8, 52 20 S 84 30, 100 14 V40 H0Z"
          fill="currentColor"
          fillOpacity={0.28}
        />
        <Path
          d="M0 32 C 22 20, 44 22, 62 30 S 88 36, 100 28 V40 H0Z"
          fill="currentColor"
          fillOpacity={0.45}
        />
      </TintedSvg>
    </>
  );
};

const PlaceholderArt = ({ size }: { size: BoxSize }) => {
  if (Math.min(size.width, size.height) < ICON_ONLY_MAX_SIZE)
    return <IconArt />;
  return <HorizonArt size={size} />;
};

// The drawing over an `Image` that is missing, loading (pulsing) or broken.
// `size` is the measured box; `null` until the first layout, when only the
// tile shows.
export const ImagePlaceholder = ({
  status,
  size,
}: {
  status: Exclude<LoadStatus, "loaded">;
  size: BoxSize | null;
}) => {
  return (
    <Box
      className={placeholderStyle({ status })}
      aria-hidden
      importantForAccessibility="no-hide-descendants"
    >
      {size !== null && <PlaceholderArt size={size} />}
    </Box>
  );
};
